import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { devWarn, devError } from '../utils/logger';
import ThemeToggle from './ThemeToggle';
import LogoImg from '../ChatGPT Image Jun 26, 2025, 12_35_25 AM.png';

const Logo = () => (
  <span className="flex items-center gap-1">
    <img
      src={LogoImg}
      alt="DevelloTech Logo"
      className="h-11 w-11 shrink-0 object-contain sm:h-12 sm:w-12"
    />
    <span
      className="text-2xl font-bold tracking-tight sm:text-3xl"
      style={{
        background: 'linear-gradient(90deg, #1e40af 0%, #06b6d4 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        color: 'transparent',
      }}
    >
      DevelloTech
    </span>
  </span>
);

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    supabase.auth
      .getSession()
      .then(({ data: { session }, error }) => {
        if (cancelled) return;
        if (error) {
          devWarn('Navbar: getSession failed', error);
          setUser(null);
          setAuthLoading(false);
          return;
        }
        setUser(session?.user ?? null);
        setAuthLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setUser(null);
          setAuthLoading(false);
        }
      });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });

    const safetyId = window.setTimeout(() => {
      if (!cancelled) setAuthLoading((s) => (s ? false : s));
    }, 10000);

    return () => {
      cancelled = true;
      window.clearTimeout(safetyId);
      subscription.unsubscribe();
    };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);





  const handleLogout = async () => {
    try {
      // Force clear user state immediately
      setUser(null);
      setDropdownOpen(false);
      
      await supabase.auth.signOut();
      
      // Force navigation with replace to prevent back button issues
      navigate('/', { replace: true });
      
      // Force page reload to ensure clean state
      setTimeout(() => {
        window.location.href = '/';
      }, 100);
    } catch (error) {
      devError('Logout error:', error);
      // Even if there's an error, force navigation
      window.location.href = '/';
    }
  };

  const handleDropdownToggle = () => {
    setDropdownOpen((open) => !open);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  if (authLoading) {
    // Show a minimal navbar while loading to prevent white screen
    return (
      <nav className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 shadow-sm backdrop-blur-md">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-[4.25rem] items-center justify-between sm:h-20">
            <div className="flex items-center">
              <div className="flex items-center cursor-pointer">
                <Logo />
              </div>
            </div>
            <div className="flex items-center">
              <div className="animate-pulse bg-gray-200 h-8 w-24 rounded"></div>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 shadow-sm backdrop-blur-md">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-[4.25rem] items-center justify-between sm:h-20">
          <div className="flex items-center">
            <div 
              className="flex items-center cursor-pointer" 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <Logo />
            </div>
          </div>
          {/* LOGGED IN: Account button with dropdown on top right */}
          {user ? (
            <div className="relative flex items-center gap-2" ref={dropdownRef}>
              <ThemeToggle compact />
              {/* Account Button and Dropdown */}
              <button
                type="button"
                className="flex items-center rounded-xl border border-slate-200/90 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition-colors hover:border-primary/25 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                onClick={handleDropdownToggle}
              >
                Account
                <svg className="ml-2 h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {dropdownOpen && (
                <div className="absolute right-0 top-full z-50 mt-2 w-52 overflow-hidden rounded-2xl border border-slate-100 bg-white py-1 shadow-xl shadow-slate-900/10">
                  <Link
                    to="/dashboard"
                    className="block w-full px-4 py-2.5 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
                  >
                    Dashboard
                  </Link>
                  <button
                    type="button"
                    className="block w-full px-4 py-2.5 text-left text-sm font-semibold text-red-600 transition-colors hover:bg-red-50"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="hidden items-center gap-1 md:flex">
              <Link
                to="/"
                className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
                  location.pathname === '/' ? 'bg-primary/10 text-primary' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                Home
              </Link>
              <Link
                to="/plans"
                className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
                  location.pathname === '/plans' ? 'bg-primary/10 text-primary' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                Pricing
              </Link>
              <Link
                to="/account"
                className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
                  location.pathname === '/account' ? 'bg-primary/10 text-primary' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                Account
              </Link>
            </div>
          )}
          {/* Mobile menu stays as before for logged out */}
          {!user && (
            <div className="md:hidden">
              <button
                onClick={toggleMobileMenu}
                className="rounded-lg p-2 text-gray-700 transition-colors hover:bg-slate-100 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>
        {/* Mobile menu only for logged out users */}
        {!user && isMobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200/80">
            <div className="flex flex-col px-2 pb-4 pt-2 space-y-1">
              <Link
                to="/"
                className={`block rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${location.pathname === '/' ? 'bg-primary/10 text-primary' : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'}`}
                onClick={toggleMobileMenu}
              >
                Home
              </Link>
              <Link
                to="/plans"
                className={`block rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${location.pathname === '/plans' ? 'bg-primary/10 text-primary' : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'}`}
                onClick={toggleMobileMenu}
              >
                Pricing
              </Link>
              <Link
                to="/account"
                className={`block rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${location.pathname === '/account' ? 'bg-primary/10 text-primary' : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'}`}
                onClick={toggleMobileMenu}
              >
                Account
              </Link>
              <Link
                to="/account"
                className="mt-2 block rounded-xl bg-primary px-4 py-3 text-center text-sm font-bold text-white transition-colors hover:bg-primary/90"
                onClick={toggleMobileMenu}
              >
                Get Started Free
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;